import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Download, LogOut, RefreshCw, Loader2, Inbox, Clock, Save, Check } from "lucide-react";
import { brand } from "../content/siteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const token = localStorage.getItem("otp_admin_token");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [settingsError, setSettingsError] = useState("");

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const logout = useCallback(() => {
    localStorage.removeItem("otp_admin_token");
    navigate("/admin/login");
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/leads`, auth);
      setLeads(data);
      const s = await axios.get(`${API}/admin/reminder-settings`, auth);
      setSettings(s.data);
    } catch (err) {
      if (err?.response?.status === 401) logout();
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logout]);

  const saveSettings = async () => {
    setSavingSettings(true);
    setSettingsError("");
    setSettingsSaved(false);
    try {
      const { data } = await axios.put(`${API}/admin/reminder-settings`, {
        enabled: settings.enabled,
        reminder1_hours: Number(settings.reminder1_hours),
        reminder2_hours: Number(settings.reminder2_hours),
      }, auth);
      setSettings(data);
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 2500);
    } catch (err) {
      if (err?.response?.status === 401) return logout();
      setSettingsError(err?.response?.data?.detail || "Could not save. Please try again.");
    } finally {
      setSavingSettings(false);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [token, navigate, load]);

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/leads/export`, { ...auth, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "otp-leads.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (err?.response?.status === 401) logout();
    }
  };

  const filtered = leads.filter((l) => filter === "all" || l.intent === filter);
  const counts = {
    all: leads.length,
    prospectus: leads.filter((l) => l.intent === "prospectus").length,
    consultation: leads.filter((l) => l.intent === "consultation").length,
    cohort: leads.filter((l) => l.intent === "cohort").length,
  };

  return (
    <div className="min-h-screen bg-offwhite">
      <header className="bg-navy border-b border-hair-dark">
        <div className="container-page flex items-center justify-between h-16">
          <div>
            <p className="font-display font-extrabold text-white text-sm tracking-tight">{brand.shortName} Leads Dashboard</p>
            <p className="text-white/50 text-[11px]">{brand.name}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="admin-refresh" className="p-2.5 text-white/70 hover:text-white transition-colors" aria-label="Refresh">
              <RefreshCw className="h-4 w-4" />
            </button>
            <button onClick={exportCsv} data-testid="admin-export" className="inline-flex items-center gap-2 bg-teal text-white text-sm font-display font-semibold rounded-sm px-4 py-2.5 hover:bg-[#08929f] transition-colors">
              <Download className="h-4 w-4" /> Export CSV
            </button>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 border border-hair-dark text-white/80 text-sm font-display font-semibold rounded-sm px-4 py-2.5 hover:bg-white/5 transition-colors">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="container-page py-10">
        {settings && (
          <div data-testid="reminder-settings" className="mb-8 border border-hair rounded-sm bg-white p-6">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="h-4 w-4 text-teal" />
              <p className="font-display font-semibold text-navy text-sm">Abandoned checkout reminders</p>
            </div>
            <p className="text-slate-ink text-xs mb-5">Automatically email people who start checkout but don't finish, with a one-click link to complete their enrolment.</p>
            <div className="flex flex-wrap items-end gap-6">
              <label className="flex items-center gap-2 cursor-pointer pb-2">
                <input
                  type="checkbox"
                  data-testid="reminder-enabled"
                  className="h-4 w-4 accent-[#0AADBD]"
                  checked={settings.enabled}
                  onChange={(e) => setSettings((s) => ({ ...s, enabled: e.target.checked }))}
                />
                <span className="text-sm text-navy font-display font-semibold">Enabled</span>
              </label>
              <div>
                <label className="block text-xs font-display font-semibold text-navy mb-1.5">First reminder (hours)</label>
                <input
                  type="number" min="1" step="1"
                  data-testid="reminder1-hours"
                  className="w-32 border border-hair rounded-sm px-3 py-2 text-sm text-navy focus:outline-none focus:border-teal"
                  value={settings.reminder1_hours}
                  onChange={(e) => setSettings((s) => ({ ...s, reminder1_hours: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-xs font-display font-semibold text-navy mb-1.5">Second reminder (hours)</label>
                <input
                  type="number" min="1" step="1"
                  data-testid="reminder2-hours"
                  className="w-32 border border-hair rounded-sm px-3 py-2 text-sm text-navy focus:outline-none focus:border-teal"
                  value={settings.reminder2_hours}
                  onChange={(e) => setSettings((s) => ({ ...s, reminder2_hours: e.target.value }))}
                />
              </div>
              <button
                onClick={saveSettings}
                data-testid="reminder-save"
                disabled={savingSettings}
                className="inline-flex items-center gap-2 bg-navy text-white text-sm font-display font-semibold rounded-sm px-4 py-2.5 hover:bg-navy/90 transition-colors disabled:opacity-60"
              >
                {savingSettings ? <Loader2 className="h-4 w-4 animate-spin" /> : settingsSaved ? <Check className="h-4 w-4" /> : <Save className="h-4 w-4" />}
                {settingsSaved ? "Saved" : "Save"}
              </button>
            </div>
            {settingsError && <p className="mt-3 text-xs text-[#b91c1c]" data-testid="reminder-error">{settingsError}</p>}
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-6">
          {["all", "prospectus", "consultation", "cohort"].map((f) => (
            <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
              className={`text-sm font-display font-semibold rounded-sm px-4 py-2 border transition-colors ${
                filter === f ? "bg-navy text-white border-navy" : "bg-white text-navy border-hair hover:border-teal"
              }`}>
              {f.charAt(0).toUpperCase() + f.slice(1)} <span className="opacity-60">({counts[f]})</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-ink"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-slate-ink" data-testid="admin-empty">
            <Inbox className="h-10 w-10 opacity-40" />
            <p className="mt-4 text-sm">No leads yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto border border-hair rounded-sm bg-white" data-testid="admin-leads-table">
            <table className="w-full text-sm border-collapse min-w-[900px]">
              <thead>
                <tr className="bg-offwhite border-b border-hair text-left">
                  {["Submitted", "Intent", "Name", "Work email", "Job title", "Organisation", "Team size", "Phone", "Role", "Transform goal"].map((h) => (
                    <th key={h} className="p-3 font-display font-semibold text-navy text-xs uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => (
                  <tr key={l.id} className="border-b border-hair hover:bg-offwhite">
                    <td className="p-3 text-slate-ink whitespace-nowrap">{new Date(l.submitted_at).toLocaleString("en-AU")}</td>
                    <td className="p-3">
                      <span className={`text-xs font-semibold px-2 py-1 rounded-sm ${
                        l.intent === "consultation" ? "bg-gold/20 text-[#8a6d1f]"
                        : l.intent === "cohort" ? "bg-navy/10 text-navy"
                        : "bg-teal/15 text-[#087b86]"}`}>
                        {l.intent}
                      </span>
                    </td>
                    <td className="p-3 text-navy font-medium whitespace-nowrap">{l.first_name} {l.last_name}</td>
                    <td className="p-3 text-navy"><a href={`mailto:${l.work_email}`} className="hover:text-teal">{l.work_email}</a></td>
                    <td className="p-3 text-slate-ink">{l.job_title}</td>
                    <td className="p-3 text-slate-ink">{l.organisation}</td>
                    <td className="p-3 text-slate-ink whitespace-nowrap">{l.cohort_size || "—"}</td>
                    <td className="p-3 text-slate-ink whitespace-nowrap">{l.phone || "—"}</td>
                    <td className="p-3 text-slate-ink">{l.role || "—"}</td>
                    <td className="p-3 text-slate-ink max-w-[240px] truncate" title={l.transform_goal}>{l.transform_goal || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
